/**
 * PummelSkillEffect — Grit hurtmore-path volley skill.
 *
 * Fires four force spikes in rapid succession during the active phase.
 * Each spike deals base damage independently and can trigger Unrelenting's
 * interrupt check, Primed's consecutive hit bonus, and Momentum's combo
 * stack bonus via UpgradeEffectResolver.
 *
 * The active phase uses onUpdate() with an internal spike timer to fire
 * each spike at fixed intervals. The player cannot cancel mid-volley.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Pummel
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PummelDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  /** Called after each spike deals damage. Integration layer provides the real callback. */
  readonly onSpikeHit?: (targetId: string) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PummelConfig {
  readonly windupMs: number;
  readonly activeMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseDamagePerSpike: number;
  readonly spikeCount: number;
  readonly spikeIntervalMs: number;
}

const DEFAULT_CONFIG: PummelConfig = {
  windupMs: 100,
  activeMs: 1200,       // Full volley window
  recoveryMs: 300,
  cooldownMs: 2000,     // "long" cooldown tier
  baseDamagePerSpike: 20,
  spikeCount: 4,
  spikeIntervalMs: 300, // ~4 spikes over 1.2s
};

// ---------------------------------------------------------------------------
// Spike result
// ---------------------------------------------------------------------------

export interface SpikeResult {
  readonly index: number;
  readonly damage: number;
  readonly timeMs: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PummelSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'pummel';

  protected readonly phaseSequence: readonly SkillPhase[] =
    ['windup', 'active', 'recovery', 'cooldown'];

  private readonly deps: PummelDeps;
  private readonly config: PummelConfig;

  private _spikesFired = 0;
  private _spikeTimer = 0;
  private _spikeResults: SpikeResult[] = [];
  private _totalDamage = 0;
  private _volleyActiveTimeMs = 0;

  // Combo tracking (Primed + Momentum upgrades)
  private _consecutiveHits = 0;
  private _lastHitTimestampMs = 0;
  private _resolvedConsecutiveHitBonus = 0;
  private _resolvedConsecutiveHitThreshold = 0;
  private _resolvedComboStackBonus = 0;
  private _resolvedComboResetWindowMs = 0;

  constructor(deps: PummelDeps, config: Partial<PummelConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.activeMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onActivate(): void {
    this._spikesFired = 0;
    this._spikeTimer = 0;
    this._spikeResults = [];
    this._totalDamage = 0;
    this._volleyActiveTimeMs = 0;

    // Resolve combo upgrade values once per activation
    this._resolvedConsecutiveHitBonus = this.deps.resolver.getMaxEffect(
      'pummel', 'consecutiveHitBonus',
    ) ?? 0;
    this._resolvedConsecutiveHitThreshold = this.deps.resolver.getMaxEffect(
      'pummel', 'consecutiveHitThreshold',
    ) ?? 0;
    this._resolvedComboStackBonus = this.deps.resolver.getMaxEffect(
      'pummel', 'comboStackBonus',
    ) ?? 0;
    this._resolvedComboResetWindowMs = this.deps.resolver.getMaxEffect(
      'pummel', 'comboResetWindowMs',
    ) ?? 0;
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      // Fire first spike immediately on entering active phase
      this.fireSpike();
    }
  }

  protected onUpdate(delta: number): void {
    if (this.phase !== 'active') return;
    if (this._spikesFired >= this.config.spikeCount) return;

    this._volleyActiveTimeMs += delta;
    this._spikeTimer += delta;

    if (this._spikeTimer >= this.config.spikeIntervalMs) {
      this._spikeTimer -= this.config.spikeIntervalMs;
      this.fireSpike();
    }
  }

  private fireSpike(): void {
    if (this._spikesFired >= this.config.spikeCount) return;

    // Reset combo counter if the window has elapsed since last hit
    const now = this._volleyActiveTimeMs;
    if (
      this._resolvedComboResetWindowMs > 0 &&
      this._lastHitTimestampMs > 0 &&
      (now - this._lastHitTimestampMs) > this._resolvedComboResetWindowMs
    ) {
      this._consecutiveHits = 0;
    }

    // Damage — queries hurtmore upgrades via enhancesId chain
    const damageMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'pummel', 'damage',
    );
    let damage = this.config.baseDamagePerSpike * damageMultiplier;

    // Combo — Primed consecutive-hit bonus
    if (
      this._resolvedConsecutiveHitThreshold > 0 &&
      this._consecutiveHits >= this._resolvedConsecutiveHitThreshold
    ) {
      damage += this._resolvedConsecutiveHitBonus;
    }

    // Combo — Momentum stack bonus (flat per consecutive hit)
    if (this._resolvedComboStackBonus > 0 && this._consecutiveHits > 0) {
      damage += this._resolvedComboStackBonus * this._consecutiveHits;
    }

    const result: SpikeResult = {
      index: this._spikesFired,
      damage,
      timeMs: this._volleyActiveTimeMs,
    };

    this._spikeResults.push(result);
    this._totalDamage += damage;
    this._spikesFired++;
    this._consecutiveHits++;
    this._lastHitTimestampMs = now;

    // TODO: scene-wiring — targetId should come from actual hit detection
    // against the enemy the spike collided with
    this.deps.onSpikeHit?.('unknown');

    if (this._spikesFired >= this.config.spikeCount) {
      this.markHitApplied();
    }
  }

  // --- Getters ---

  get spikesFired(): number { return this._spikesFired; }
  get spikeResults(): readonly SpikeResult[] { return this._spikeResults; }
  get totalDamage(): number { return this._totalDamage; }
  get volleyComplete(): boolean { return this._spikesFired >= this.config.spikeCount; }
  get consecutiveHits(): number { return this._consecutiveHits; }
}
